import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import './NotificationsPage.css';

// No icons — clean text-only layout

const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchNotifications = useCallback(async (pageNum = 1, reset = false) => {
    try {
      const response = await axiosInstance.get(`/notifications/history/?page=${pageNum}&page_size=20`);
      const data = response.data;
      const items = data.notifications || [];

      if (reset) {
        setNotifications(items);
        setPage(2);
      } else {
        setNotifications(prev => [...prev, ...items]);
        setPage(prev => prev + 1);
      }
      setHasMore(pageNum < (data.pagination?.pages || 1));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchNotifications(1, true).finally(() => setLoading(false));
  }, [fetchNotifications]);

  const markOneRead = async (notificationId) => {
    try {
      await axiosInstance.post('/notifications/mark-read/', { notification_ids: [notificationId] });
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      markOneRead(notification.id);
    }

    const data = notification.data || {};
    const type = data.type || notification.notification_type;

    // Promotional / non-navigable — expand inline
    if (type === 'promotional' || type === 'course_update' || type === 'system_announcement') {
      setExpandedId(prev => prev === notification.id ? null : notification.id);
      return;
    }

    // Navigate based on type
    switch (type) {
      case 'club_request_received':
      case 'club_message':
        if (data.interaction_request_id) {
          navigate(`/chat?tab=clubs&threadId=${data.interaction_request_id}`);
        } else {
          navigate('/chat?tab=clubs');
        }
        break;

      case 'chat_message':
      case 'message':
      case 'new_message':
        if (data.sender_id) {
          navigate(`/chat?tab=general&userId=${data.sender_id}`);
        } else {
          navigate('/chat?tab=general');
        }
        break;

      case 'golfer_feedback':
        if (data.feedback_id) {
          navigate(`/chat?tab=clubs&feedbackId=${data.feedback_id}`);
        } else {
          navigate('/chat?tab=clubs');
        }
        break;

      case 'member_portal_chat':
      case 'member_portal_message':
        if (data.request_id || data.play_request_id) {
          navigate(`/member-portal/messages/${data.request_id || data.play_request_id}`);
        } else {
          navigate('/member-portal/messages');
        }
        break;

      case 'messageboard_reply':
      case 'post_reply':
      case 'reply_reply':
      case 'post_voted':
      case 'post_milestone':
        if (data.post_id || data.message_id) {
          const postId = data.post_id || data.message_id;
          const postType = data.post_type;
          if (postType === 'forum' || postType === 'forum_post') {
            navigate(`/forums/usa/${postId}`);
          } else {
            navigate(`/post/${postId}`);
          }
        }
        break;

      case 'play_request':
      case 'play_request_received':
      case 'play_request_scheduled':
      case 'play_request_confirmed':
      case 'play_request_declined':
      case 'play_request_cancelled':
        if (data.request_id) {
          navigate(`/member-portal/requests/${data.request_id}`);
        } else {
          navigate('/member-portal');
        }
        break;

      case 'verification_approved':
      case 'verification_rejected':
      case 'verification_call':
        navigate('/member-portal');
        break;

      case 'referral_completed':
      case 'referral_reward_ready':
        navigate('/referral');
        break;

      case 'password_reset':
      case 'email_verified':
      case 'account_security':
        navigate('/myinfo');
        break;

      default:
        break;
    }
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    fetchNotifications(page, false).finally(() => setLoadingMore(false));
  };

  if (loading) {
    return (
      <div className="notifications-page">
        <div className="notifications-header">
          <h1 className="notifications-title">Alerts</h1>
        </div>
        <div className="notifications-loading">
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1 className="notifications-title">Alerts</h1>
      </div>

      {notifications.length === 0 ? (
        <div className="notifications-list">
          <div className="notifications-empty">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <h3>No notifications yet</h3>
            <p>You'll see club requests, messages, and activity here</p>
          </div>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map((notification) => {
            const isExpanded = expandedId === notification.id;

            return (
              <div
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-content">
                  <div className="notification-title-row">
                    <span className="notification-item-title">{notification.title}</span>
                    <div className="notification-title-right">
                      {!notification.is_read && <span className="unread-dot" />}
                      <span className="notification-time">{getTimeAgo(notification.sent_at)}</span>
                    </div>
                  </div>
                  <p className={`notification-body ${isExpanded ? 'expanded' : ''}`}>
                    {notification.body}
                  </p>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div className="load-more-container">
              <button
                className="load-more-btn"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
